import { HIDE_SCANX, HIDE_OBLOANE, WELCOME_VARIANT } from '../utils/featureFlags';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Camera, LayoutGrid, Calculator, Layers, Mic } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import SuggestionForm from '../components/welcome/SuggestionForm';

/**
 * Welcome Page - Tamplarie lemn.
 * Structura noua cu tab-uri: ScanX (doar admin), Calcul simplificat, Calcul complex.
 */
const WelcomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuest = user?.rol === 'guest';
  const isAdmin = user?.rol === 'admin';
  const showScanx = !isGuest && !HIDE_SCANX;
  const isPvc = WELCOME_VARIANT === 'pvc';

  const guestLock = () => {
    toast.error(t('welcome_constructii.guest_locked', 'Disponibil pentru utilizatorii inregistrati'), {
      action: { label: t('welcome_constructii.guest_locked_cta', 'Inregistreaza-te'), onClick: () => navigate('/register') },
    });
  };

  const goComplex = (route) => {
    if (isGuest) { guestLock(); return; }
    navigate(route);
  };

  const defaultTab = showScanx ? 'scanx' : 'simplificat';

  const ToolCard = ({ testid, icon: Icon, title, desc, onEnter, enterLabel, red, blue }) => (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
          <Icon className="h-5 w-5 text-slate-600" />
        </div>
        <h3 className={`font-bold text-xl sm:text-2xl ${red ? 'text-red-600' : 'text-slate-900'}`}>{title}</h3>
      </div>
      <p className="text-lg text-slate-900 flex-1 mb-4">{desc}</p>
      <div className="flex gap-2">
        <button
          onClick={onEnter}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-base sm:text-lg rounded-md font-semibold text-white transition-colors"
          style={{ backgroundColor: red ? '#dc2626' : blue ? '#2563eb' : '#2E7D5B' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = red ? '#b91c1c' : blue ? '#1d4ed8' : '#25664A'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = red ? '#dc2626' : blue ? '#2563eb' : '#2E7D5B'; }}
          data-testid={`${testid}-enter`}
        >
          {enterLabel}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <header className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3" data-testid="welcome-hero">
            {isPvc ? 'TAMPLARIE PVC' : t('welcome.hero_title', 'AMEL International Group')}
          </h1>
          {!isPvc && (
            <p className="text-lg sm:text-xl text-slate-900">
              {t('welcome.hero_subtitle', 'Bun venit in aplicatia de ofertare a fabricilor Amel din Romania.')}
            </p>
          )}
          <div className="mt-6 flex justify-center">
            <img
              src="/images/login-hero.jpeg"
              alt="AMEL"
              className="max-h-64 sm:max-h-80 w-auto object-contain rounded-lg shadow-md"
              data-testid="welcome-hero-image"
            />
          </div>
        </header>

        {isPvc && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="pvc-brands">
            {['Rehau', 'Veka', 'Salamander', 'Gealan'].map((brand) => (
              <button
                key={brand}
                onClick={() => goComplex('/configurator')}
                className="inline-flex items-center justify-center px-4 py-4 text-lg sm:text-xl rounded-xl font-bold text-white transition-colors shadow-sm"
                style={{ backgroundColor: '#2563eb' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
                data-testid={`pvc-brand-${brand.toLowerCase()}`}
              >
                {brand}
              </button>
            ))}
          </div>
        )}

        <Tabs defaultValue={defaultTab} className="mb-10">
          <TabsList className="w-full h-auto flex-wrap gap-2 bg-slate-100 p-1.5 rounded-xl text-slate-900" data-testid="welcome-tabs">
            {showScanx && (
              <TabsTrigger value="scanx" className="flex-1 py-3 text-base sm:text-lg font-bold text-white data-[state=active]:text-white bg-blue-600 data-[state=active]:bg-blue-700 data-[state=active]:ring-2 data-[state=active]:ring-blue-300" data-testid="welcome-tab-scanx">
                {t('welcome.tab_scanx', 'ScanX')}
              </TabsTrigger>
            )}
            {showScanx && (
              <TabsTrigger value="voce" className="flex-1 py-3 text-base sm:text-lg font-bold text-white data-[state=active]:text-white bg-violet-600 data-[state=active]:bg-violet-700 data-[state=active]:ring-2 data-[state=active]:ring-violet-300" data-testid="welcome-tab-voce">
                {t('welcome.tab_voce', 'Voce')}
              </TabsTrigger>
            )}
            <TabsTrigger value="simplificat" className="flex-1 py-3 text-base sm:text-lg font-bold text-white data-[state=active]:text-white bg-emerald-700 data-[state=active]:bg-emerald-800 data-[state=active]:ring-2 data-[state=active]:ring-emerald-300" data-testid="welcome-tab-simplificat">
              {t('welcome.tab_simplificat', 'Calcul simplificat')}
            </TabsTrigger>
            <TabsTrigger value="complex" className="flex-1 py-3 text-base sm:text-lg font-bold text-white data-[state=active]:text-white bg-red-600 data-[state=active]:bg-red-700 data-[state=active]:ring-2 data-[state=active]:ring-red-300" data-testid="welcome-tab-complex">
              {t('welcome.tab_complex', 'Calcul complex')}
            </TabsTrigger>
          </TabsList>

          {showScanx && (
            <TabsContent value="scanx" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <p className="text-lg text-slate-900 mb-4" data-testid="welcome-scanx-desc">
                  {t('welcome.tab_scanx_desc', 'Aici puteti incarca schite si aplicatia va calcula pretul pentru ferestre si usi standard.')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ToolCard
                    testid="welcome-card-scanx"
                    icon={Camera}
                    blue
                    title={t('welcome.cards.scanx.title', 'ScanX')}
                    desc={t('welcome.cards.scanx.short', 'Calcul direct de pe telefon din foto sau schita desenata de mana.')}
                    onEnter={() => navigate('/scan-x')}
                    enterLabel={t('welcome.cards.scanx.enter', 'Deschide')}
                  />
                </div>
              </div>
            </TabsContent>
          )}

          {showScanx && (
            <TabsContent value="voce" className="mt-4">
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <p className="text-lg text-slate-900 mb-4" data-testid="welcome-voce-desc">
                  {t('welcome.tab_voce_desc', 'Aici puteti dicta dimensiunile si aplicatia va calcula pretul pentru ferestre, usi si obloane.')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <ToolCard
                    testid="welcome-card-voce"
                    icon={Mic}
                    blue
                    title={t('welcome.cards.voce.title', 'Voce')}
                    desc={t('welcome.cards.voce.short', 'Calcul direct de pe telefon prin dictare vocala.')}
                    onEnter={() => navigate('/voce')}
                    enterLabel={t('welcome.cards.voce.enter', 'Deschide')}
                  />
                </div>
              </div>
            </TabsContent>
          )}

          <TabsContent value="simplificat" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <p className="text-sm text-slate-600 mb-4" data-testid="welcome-simplificat-desc">
                {t('welcome.tab_simplificat_desc', 'Aici puteti calcula oferte de pret rapid cu cateva optiuni de baza.')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!HIDE_OBLOANE && (
                <ToolCard
                  testid="welcome-card-simplificat-obloane"
                  icon={Calculator}
                  title={t('welcome.cards.calcul_simplificat_obloane.title', 'Obloane Simplificat')}
                  desc={t('welcome.cards.calcul_simplificat_obloane.short', 'Doar dimensiuni si numar de canate. Setari implicite aplicate automat.')}
                  onEnter={() => navigate('/calcul-simplificat-obloane')}
                  enterLabel={t('welcome.cards.calcul_simplificat_obloane.enter', 'Deschide')}
                />
                )}
                <ToolCard
                  testid="welcome-card-simplificat-ferestre-usi"
                  icon={Calculator}
                  title={t('welcome.cards.calcul_simplificat_ferestre_usi.title', 'Ferestre/Usi Simplificat')}
                  desc={t('welcome.cards.calcul_simplificat_ferestre_usi.short', 'Doar dimensiuni si numar bucati. Setari implicite aplicate automat.')}
                  onEnter={() => navigate('/calcul-simplificat-ferestre-usi')}
                  enterLabel={t('welcome.cards.calcul_simplificat_ferestre_usi.enter', 'Deschide')}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="complex" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <p className="text-lg text-slate-900 mb-4" data-testid="welcome-complex-desc">
                {t('welcome.tab_complex_desc', 'Aici puteti calcula oferte de pret detaliate si puteti alege optiuni suplimentare.')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!HIDE_OBLOANE && (
                <ToolCard
                  testid="welcome-card-complex-obloane"
                  icon={Layers}
                  red
                  title={t('welcome.cards.obloane_lemn.title', 'OBLOANE')}
                  desc={t('welcome.cards.obloane_lemn.short', 'Configurare completa cu lamele fixe, mobile sau tablii.')}
                  onEnter={() => goComplex('/obloane-lemn')}
                  enterLabel={t('welcome.cards.obloane_lemn.enter', 'Deschide')}
                />
                )}
                <ToolCard
                  testid="welcome-card-complex-ferestre-usi"
                  icon={LayoutGrid}
                  red
                  title={t('welcome.cards.configurator.title', 'Ferestre/Usi')}
                  desc={t('welcome.cards.configurator.short', 'Calcul aprofundat recomandat pentru desktop.')}
                  onEnter={() => goComplex('/configurator')}
                  enterLabel={t('welcome.cards.configurator.enter', 'Deschide')}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-6">
          <SuggestionForm />
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;
